import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
    getInsertMark,
    getSubstituteMark,
    getInsertMarkContent,
    isChoosenCorrectionWord,
    getSubstituteMarkContent,
} from '../../../../../utils/shared/marksBuilderUtils';
import { getMarksBuilderConfiguration } from '../../../../../utils/reading/marksBuilderUtils';
import './Word.scss';

const Word = ({
    sentenceLength,
    word,
    isNewLine,
    isNewPage,
    numberPage,
    contentReference,
    marks,
    widget,
    isItalic,
    wordPosition,
    startPosition,
    endPosition,
    onKeyDownHandler,
    onClickHandler,
    getMarksClassNames,
    onPressHoldHandler,
}) => {
    const wordRef = useRef();
    const wordInsertRef = useRef();

    const { isStartOfReading, isEndOfReading, isEnd, paginationItem } =
        getMarksBuilderConfiguration(
            wordPosition,
            sentenceLength,
            endPosition,
            startPosition,
            numberPage,
        );

    const insertMark = getInsertMark(marks);
    const substituteMark = getSubstituteMark(marks);

    const {
        canChosen,
        insertClassName,
        userCorrectionClassName,
        correctedClassName,
        wordClassName,
        omitClassName,
        repeatClassName,
        insertRepeatClassName,
        substituteSelfCorrectClassName,
        insertSelfCorrectClassName,
        substituteTextClassName,
        activeWordClassName,
        activeInsertWordClassName,
        correctionClassName,
    } = getMarksClassNames(marks, wordPosition, isEnd, isItalic);

    const insertWordWrapperClasses = `${insertRepeatClassName} ${wordClassName} ${activeInsertWordClassName} ${canChosen}`;
    const insertWordClasses = `${insertSelfCorrectClassName} ${insertClassName} ${correctionClassName}`;
    const wordWrapperClasses = `${repeatClassName} ${wordClassName} ${activeWordClassName}`;
    const wordClasses = `${substituteSelfCorrectClassName} ${omitClassName} ${correctedClassName}`;
    const substituteWordClasses = `${substituteSelfCorrectClassName} ${substituteTextClassName}`;
    const isClickable = isChoosenCorrectionWord(endPosition, wordPosition);

    const insertMarkContent =
        insertMark &&
        getInsertMarkContent({
            insertMark,
            wordWrapperProperties: {
                tabIndex: '0',
                role: 'button',
                className: canChosen,
                onClick: () => {
                    if (isClickable)
                        onClickHandler(
                            true,
                            false,
                            wordInsertRef,
                            contentReference,
                            marks,
                            wordPosition,
                        );
                },
            },
            insertWordWrapperClasses,
            insertWordClasses,
        });
    const substituteMarkContent = getSubstituteMarkContent({
        word,
        wordClasses,
        substituteMark,
        wordWrapperClasses,
        substituteWordClasses,
    });

    return (
        <>
            {isNewPage && <div className="sentence-pagination-item">{paginationItem}</div>}
            {isNewLine && <br />}
            {isStartOfReading && <span className="range-reading-position">[</span>}
            {insertMark && (
                <>
                    <span
                        tabIndex="0"
                        role="button"
                        ref={wordInsertRef}
                        className="word-container"
                        onKeyDown={onKeyDownHandler}
                    >
                        {insertMarkContent}
                    </span>
                </>
            )}
            {!isEnd && (
                <span
                    tabIndex="0"
                    role="button"
                    ref={wordRef}
                    className={`word-container ${userCorrectionClassName} ${correctionClassName}`}
                    onKeyDown={onKeyDownHandler}
                >
                    <div
                        tabIndex="0"
                        role="button"
                        className={canChosen}
                        onClick={() => {
                            if (isClickable)
                                onClickHandler(
                                    false,
                                    false,
                                    wordRef,
                                    contentReference,
                                    marks,
                                    wordPosition,
                                );
                        }}
                        {...onPressHoldHandler(wordRef, contentReference, marks, wordPosition)}
                    >
                        {substituteMarkContent}
                    </div>
                </span>
            )}
            {isEndOfReading && <span className="range-reading-position">]</span>}
            {widget}
        </>
    );
};

Word.defaultProps = {
    sentenceLength: null,
    word: '',
    isNewLine: false,
    isNewPage: false,
    numberPage: null,
    marks: [],
    isItalic: false,
    wordPosition: null,
    startPosition: null,
    endPosition: null,
    widget: <></>,
    contentReference: {},
    onKeyDownHandler: () => {},
    onPressHoldHandler: () => {},
    onClickHandler: () => {},
    getMarksClassNames: () => {},
};

Word.propTypes = {
    sentenceLength: PropTypes.number,
    word: PropTypes.string,
    isNewLine: PropTypes.bool,
    isNewPage: PropTypes.bool,
    numberPage: PropTypes.number,
    marks: PropTypes.array,
    widget: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
    contentReference: PropTypes.object,
    isItalic: PropTypes.bool,
    wordPosition: PropTypes.number,
    startPosition: PropTypes.number,
    endPosition: PropTypes.number,
    onKeyDownHandler: PropTypes.func,
    onPressHoldHandler: PropTypes.func,
    onClickHandler: PropTypes.func,
    getMarksClassNames: PropTypes.func,
};

export default Word;
